/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { get } from 'lodash'
import moment from 'moment'

import useInterval from '../hooks/useInterval'

import VNLoader from '../VNLoader/components/VNLoader'
import { setRoute } from '../VNRoutes/ActionCreators'

import { getCurrentMenu, getIsKiosk } from '../selectors/menus'

import { clearPromotionsOnOrder } from '../actions/promotion'

import { getNetworkAvailableValue } from '../utils/networkConnected'
import { getHasRecoveryData } from '../VNTransactions/Selectors'
import useDeviceIdentifiers from '../VNUtil/hooks/useDeviceIdentifiers'
import useLogin from '../VNLogin/hooks/useLogin'
import { getLastBaseLoadCompleted } from '../VNDataLoadManager/Selectors'
import { startBaseLoad } from '../VNDataLoadManager/ActionCreators'
import { clearImageCache } from '../VNImageCache/utils'
import { isNetworkError } from '../VNUtil/Errors'
import { hasAttendants } from '../selectors/attendant'
import { setNetworkAvailable } from '../actions/config'

const DeviceLogin = () => {
  const ONE_MINUTE = 60000
  const ONE_HOUR = ONE_MINUTE * 60
  const dispatch = useDispatch()
  const hasConfig = useSelector((state) => get(state, 'config.hasConfig', false))
  const isKiosk = useSelector(getIsKiosk)
  const lastBaseLoad = useSelector(getLastBaseLoadCompleted)
  const isInRecoveryMode = useSelector(getHasRecoveryData)
  const hasLoadedAttendants = useSelector(hasAttendants)
  const hasMenuData = useSelector(getCurrentMenu)
  const hasUsableData = useMemo(() => hasMenuData?.uuid && hasLoadedAttendants, [hasMenuData, hasLoadedAttendants])
  const { getConditionalDeviceId } = useDeviceIdentifiers()
  const { login, updateTokens } = useLogin()

  const onLoginSuccess = (response) => {
    updateTokens(response)

    // stop here when
    if (!hasConfig && lastBaseLoad === undefined) {
      dispatch(startBaseLoad())
      return
    }

    /**
     * FIXME:
     * Without this timeout, the revenue center setup screen gets a 500 from stadium because the
     * the updateTokens() method (above) hasn't completed yet, so the token doesn't get passed when
     * fetching the app device configuration.
     * This is a good excuse/reason for us to move everything that uses
     * localStorages getItem() and setItem() methods into the global redux state.
     */
    setTimeout(() => {
      if (isKiosk) {
        //In kiosk mode, clear image cache before going to the attract loop
        clearImageCache(null, true).finally(() => {
          dispatch(setRoute('/kiosk/attract_loop'))
        })
        return
      }
      dispatch(setRoute('/user-login', {}))
    }, 1000)
  }

  const onLoginFailure = (err) => {
    if (isNetworkError(err) && hasUsableData) {
      dispatch(setNetworkAvailable(false))
      dispatch(setRoute('/user-login'))
      return
    }

    if (localStorage.getItem('accessToken') === null) {
      dispatch(setRoute('/register-device'))
    } else {
      dispatch(setRoute('/revenue-center-setup'))
    }
  }

  const nonRedirectFailureResponse = (err) => {
    console.warn(err, 'Portico failed to authorize request')
  }

  useEffect(() => {
    // summit-dependent deviceId
    if (!getConditionalDeviceId()) {
      // initialize device registration
      dispatch(setRoute('/register-device'))
      return
    }

    if (isInRecoveryMode) {
      return
    }

    if (!getNetworkAvailableValue() && localStorage.getItem('accessToken') && hasConfig) {
      dispatch(setRoute('/user-login'))
      return
    }

    login(onLoginSuccess, onLoginFailure)
  }, [])

  useEffect(() => {
    dispatch(clearPromotionsOnOrder())
  }, [])

  useInterval(() => {
    const rawExpiresAt = localStorage.getItem('expiresAt')
    if (rawExpiresAt === null || rawExpiresAt === undefined) {
      login(updateTokens, nonRedirectFailureResponse)
      return
    }

    const expiresAt = moment(rawExpiresAt)
    const tokenMillisecondsRemaining = expiresAt.diff(moment())
    if (tokenMillisecondsRemaining < ONE_HOUR) {
      login(updateTokens, nonRedirectFailureResponse)
    }
  }, ONE_MINUTE)

  return <VNLoader />
}

export default DeviceLogin
